import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, Tag, Space, Tooltip, Button, Tabs, Checkbox, Modal, List, Input, Form, message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, FileTextOutlined } from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;

const Monitoring = () => {
  const [teams, setTeams] = useState([]);
  const [irregularConvocations, setIrregularConvocations] = useState([]);
  const [irregularSubscriptions, setIrregularSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('recent-teams');
  const [filters, setFilters] = useState({
    noGroupJid: false,
    noPlayers: false,
    noOpenGame: false,
    noGameSettings: false,
    noConvocations: false,
    noActiveSubscription: false,
    noSupportLog: false
  });
  const [supportModalVisible, setSupportModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [supportLogs, setSupportLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [addLogModalVisible, setAddLogModalVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchRecentTeams = async () => {
    setLoading(true);
    try {
      const response = await api.get('/monitoring/recent-teams');
      setTeams(response.data);
    } catch (error) {
      console.error('Erro ao carregar times recentes:', error);
    }
    setLoading(false);
  };

  const fetchIrregularConvocations = async () => {
    setLoading(true);
    try {
      const response = await api.get('/monitoring/irregular-convocations');
      setIrregularConvocations(response.data);
    } catch (error) {
      console.error('Erro ao carregar convocações irregulares:', error);
    }
    setLoading(false);
  };

  const fetchIrregularSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/monitoring/irregular-subscriptions');
      setIrregularSubscriptions(response.data);
    } catch (error) {
      console.error('Erro ao carregar assinaturas irregulares:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'recent-teams') {
      fetchRecentTeams();
    } else if (activeTab === 'irregular-convocations') {
      fetchIrregularConvocations();
    } else if (activeTab === 'irregular-subscriptions') {
      fetchIrregularSubscriptions();
    }
  }, [activeTab]);

  const renderCheck = (check, label) => (
    <Tooltip title={label}>
      {check ? (
        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
      ) : (
        <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
      )}
    </Tooltip>
  );

  const columns = [
    {
      title: 'Time',
      dataIndex: 'game_name',
      key: 'game_name',
      width: 200,
      render: (text, record) => (
        <div>
          <a href={`/customers/${record.id}/ficha`} target="_blank" rel="noreferrer" style={{ fontWeight: 'bold' }}>{text}</a>
          <div style={{ fontSize: 12, color: '#666' }}>
            {record.created_at ? dayjs(record.created_at).format('DD/MM/YYYY HH:mm') : `ID: ${record.id}`}
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>
            Tel: {record.organizer_phone || 'N/A'}
          </div>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'ATIVO' ? 'green' : 'red'}>
          {status}
        </Tag>
      )
    },
    {
      title: 'Checks',
      key: 'checks',
      width: 200,
      render: (_, record) => (
        <Space>
          {renderCheck(record.checks.hasGroupJid, 'GroupJID configurado')}
          {renderCheck(record.checks.hasPlayers, `Jogadores cadastrados (${record.playersCount})`)}
          {renderCheck(record.checks.hasOpenGame, `Jogos abertos (${record.openGamesCount})`)}
          {renderCheck(record.checks.hasGameSettings, 'Configurações de jogo')}
          {renderCheck(record.checks.hasConvocations, 'Convocações enviadas')}
          {renderCheck(record.checks.hasActiveSubscription, 'Subscription ativa')}
        </Space>
      )
    },
    {
      title: 'Detalhes',
      key: 'details',
      render: (_, record) => {
        const issues = [];
        if (!record.checks.hasGroupJid) issues.push('GroupJID não configurado');
        if (!record.checks.hasPlayers) issues.push('Nenhum jogador cadastrado');
        if (!record.checks.hasOpenGame) issues.push('Nenhum jogo aberto');
        if (!record.checks.hasGameSettings) issues.push('Configurações de jogo não definidas');
        if (!record.checks.hasConvocations) issues.push('Nenhuma convocação enviada');
        if (!record.checks.hasActiveSubscription) issues.push('Subscription inativa');
        
        return (
          <div>
            {issues.length === 0 ? (
              <Tag color="green">Tudo OK</Tag>
            ) : (
              issues.map((issue, index) => (
                <Tag key={index} color="orange" style={{ marginBottom: 4 }}>
                  {issue}
                </Tag>
              ))
            )}
          </div>
        );
      }
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Button 
          size="small" 
          icon={<FileTextOutlined />}
          onClick={() => openSupportModal(record)}
        >
          Gerenciar Atendimento
        </Button>
      )
    }
  ];

  const getFilteredTeams = () => {
    const hasActiveFilters = Object.values(filters).some(f => f);
    if (!hasActiveFilters) return teams;
    
    return teams.filter(team => {
      const matchesFilters = [];
      
      if (filters.noGroupJid) matchesFilters.push(!team.checks.hasGroupJid);
      if (filters.noPlayers) matchesFilters.push(!team.checks.hasPlayers);
      if (filters.noOpenGame) matchesFilters.push(!team.checks.hasOpenGame);
      if (filters.noGameSettings) matchesFilters.push(!team.checks.hasGameSettings);
      if (filters.noConvocations) matchesFilters.push(!team.checks.hasConvocations);
      if (filters.noActiveSubscription) matchesFilters.push(!team.checks.hasActiveSubscription);
      if (filters.noSupportLog) matchesFilters.push(!team.checks.hasSupportLog);
      
      // Retorna true se TODAS as condições selecionadas forem atendidas
      return matchesFilters.every(match => match === true);
    });
  };

  const handleFilterChange = (filterKey, checked) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: checked
    }));
  };

  const openSupportModal = async (customer) => {
    setSelectedCustomer(customer);
    setSupportModalVisible(true);
    await loadSupportLogs(customer.id);
  };

  const loadSupportLogs = async (customerId) => {
    setLoadingLogs(true);
    try {
      const response = await api.get(`/support-logs/${customerId}`);
      setSupportLogs(response.data);
    } catch (error) {
      console.error('Erro ao carregar atendimentos:', error);
    }
    setLoadingLogs(false);
  };

  const handleAddLog = async (values) => {
    try {
      await api.post(`/support-logs/${selectedCustomer.id}`, values);
      message.success('Atendimento registrado com sucesso');
      form.resetFields();
      setAddLogModalVisible(false);
      await loadSupportLogs(selectedCustomer.id);
    } catch (error) {
      message.error('Erro ao registrar atendimento');
    }
  };

  const irregularColumns = [
    {
      title: 'Time',
      dataIndex: 'game_name',
      key: 'game_name',
      render: (text, record) => (
        <div>
          <a href={`/customers/${record.id}/ficha`} target="_blank" rel="noreferrer" style={{ fontWeight: 'bold' }}>{text}</a>
          <div style={{ fontSize: 12, color: '#666' }}>ID: {record.id}</div>
          <div style={{ fontSize: 12, color: '#666' }}>Tel: {record.organizer_phone || 'N/A'}</div>
        </div>
      )
    },
    {
      title: 'Jogo',
      dataIndex: 'date_of_game',
      key: 'date_of_game',
      render: (text, record) => (
        <div>
          <div>{text}</div>
          <Tag color="red">Convocação Irregular</Tag>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'ATIVO' ? 'green' : 'red'}>
          {status}
        </Tag>
      )
    }
  ];

  const subscriptionColumns = [
    {
      title: 'Time',
      dataIndex: 'game_name',
      key: 'game_name',
      render: (text, record) => (
        <div>
          <a href={`/customers/${record.id}/ficha`} target="_blank" rel="noreferrer" style={{ fontWeight: 'bold' }}>{text}</a>
          <div style={{ fontSize: 12, color: '#666' }}>ID: {record.id}</div>
          <div style={{ fontSize: 12, color: '#666' }}>Tel: {record.organizer_phone || 'N/A'}</div>
        </div>
      )
    },
    {
      title: 'Último Pagamento',
      dataIndex: 'payment_at',
      key: 'payment_at',
      render: (text) => (
        <div>
          <div>{text ? dayjs(text).format('DD/MM/YYYY') : 'N/A'}</div>
          <Tag color="red">Expirada</Tag>
        </div>
      )
    },
    {
      title: 'Jogos após Expiração',
      dataIndex: 'games_after_expiration',
      key: 'games_after_expiration',
      render: (count) => (
        <Tag color="orange">{count} jogos</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'ATIVO' ? 'green' : 'red'}>
          {status}
        </Tag>
      )
    }
  ];

  const tabItems = [
    {
      key: 'recent-teams',
      label: 'Novos Times',
      children: (
        <div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <strong>Filtrar por irregularidades:</strong>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
              <Checkbox 
                checked={filters.noGroupJid}
                onChange={(e) => handleFilterChange('noGroupJid', e.target.checked)}
              >
                Sem GroupJID
              </Checkbox>
              <Checkbox 
                checked={filters.noPlayers}
                onChange={(e) => handleFilterChange('noPlayers', e.target.checked)}
              >
                Sem Jogadores
              </Checkbox>
              <Checkbox 
                checked={filters.noOpenGame}
                onChange={(e) => handleFilterChange('noOpenGame', e.target.checked)}
              >
                Sem Jogos Abertos
              </Checkbox>
              <Checkbox 
                checked={filters.noGameSettings}
                onChange={(e) => handleFilterChange('noGameSettings', e.target.checked)}
              >
                Sem Configurações
              </Checkbox>
              <Checkbox 
                checked={filters.noConvocations}
                onChange={(e) => handleFilterChange('noConvocations', e.target.checked)}
              >
                Sem Convocações
              </Checkbox>
              <Checkbox 
                checked={filters.noActiveSubscription}
                onChange={(e) => handleFilterChange('noActiveSubscription', e.target.checked)}
              >
                Sem Subscription Ativa
              </Checkbox>
              <Checkbox 
                checked={filters.noSupportLog}
                onChange={(e) => handleFilterChange('noSupportLog', e.target.checked)}
              >
                Sem Atendimento
              </Checkbox>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button 
                size="small" 
                onClick={() => setFilters({
                  noGroupJid: false,
                  noPlayers: false,
                  noOpenGame: false,
                  noGameSettings: false,
                  noConvocations: false,
                  noActiveSubscription: false,
                  noSupportLog: false
                })}
              >
                Limpar Filtros
              </Button>
              <Space>
                <span>Legenda:</span>
                {renderCheck(true, '')} <span>OK</span>
                {renderCheck(false, '')} <span>Pendente</span>
              </Space>
            </div>
          </div>
          <Table
            columns={columns}
            dataSource={getFilteredTeams()}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 20 }}
            scroll={{ x: 800 }}
            rowClassName={(record) => 
              record.checks.hasSupportLog ? 'team-with-support' : ''
            }
          />
        </div>
      )
    },
    {
      key: 'irregular-convocations',
      label: 'Convocações Irregulares',
      children: (
        <Table
          columns={irregularColumns}
          dataSource={irregularConvocations}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 800 }}
        />
      )
    },
    {
      key: 'irregular-subscriptions',
      label: 'Assinaturas Irregulares',
      children: (
        <Table
          columns={subscriptionColumns}
          dataSource={irregularSubscriptions}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 800 }}
        />
      )
    }
  ];

  const handleRefresh = () => {
    if (activeTab === 'recent-teams') {
      fetchRecentTeams();
    } else if (activeTab === 'irregular-convocations') {
      fetchIrregularConvocations();
    } else if (activeTab === 'irregular-subscriptions') {
      fetchIrregularSubscriptions();
    }
  };

  return (
    <div>
      <style>
        {`
          .team-with-support {
            background-color: #f6ffed !important;
          }
          .team-with-support:hover {
            background-color: #d9f7be !important;
          }
        `}
      </style>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 16 
      }}>
        <Title level={2} style={{ margin: 0 }}>
          Monitoramento
        </Title>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={handleRefresh}
          loading={loading}
        >
          Atualizar
        </Button>
      </div>

      <Card>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>

      {/* Modal de Atendimentos */}
      <Modal
        title={`Atendimentos - ${selectedCustomer?.game_name}`}
        open={supportModalVisible}
        onCancel={() => setSupportModalVisible(false)}
        width={700}
        footer={[
          <Button key="add" type="primary" onClick={() => setAddLogModalVisible(true)}>
            Adicionar Atendimento
          </Button>,
          <Button key="close" onClick={() => setSupportModalVisible(false)}>
            Fechar
          </Button>
        ]}
      >
        <List
          loading={loadingLogs}
          dataSource={supportLogs}
          locale={{ emptyText: 'Nenhum atendimento registrado' }}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{item.author_name}</span>
                    <span style={{ fontSize: 12, color: '#999' }}>
                      {dayjs(item.created_at).format('DD/MM/YYYY HH:mm')}
                    </span>
                  </div>
                }
                description={item.description}
              />
            </List.Item>
          )}
        />
      </Modal>

      {/* Modal de Adicionar Atendimento */}
      <Modal
        title="Adicionar Atendimento"
        open={addLogModalVisible}
        onCancel={() => {
          setAddLogModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="Salvar"
        cancelText="Cancelar"
      >
        <Form form={form} onFinish={handleAddLog} layout="vertical">
          <Form.Item
            name="description"
            label="Descrição"
            rules={[{ required: true, message: 'Digite a descrição do atendimento' }]}
          >
            <Input.TextArea rows={4} placeholder="Descreva o atendimento realizado..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Monitoring;